import api from './api';
import { assertCurrentSession, captureSession } from '../utils/authSession';

const idPattern = /^[a-f\d]{24}$/;
const revisionPattern = /^[a-f\d]{64}$/;
const completionError = () =>
  new Error('Could not confirm completion. Refresh shipments before trying again.');

async function completeShipment(shipment) {
  const session = captureSession();
  const id = shipment?._id;
  const orderNumber = shipment?.orderId || shipment?.order?.orderId;
  try {
    if (!idPattern.test(id) || !session.userId || !session.hadToken || !orderNumber)
      throw completionError();
    const config = { sessionBinding: session, timeout: 20000 };
    const { data } = await api.get(`/admin/shipments/${id}/status-options`, config);
    assertCurrentSession(session);
    const before = data?.data;
    if (
      before?.shipmentStatusValidated !== true ||
      before._id !== id ||
      before.orderNumber !== orderNumber ||
      !idPattern.test(before.orderId) ||
      before.orderStatus !== 'shipped' ||
      !['pending', 'in_transit'].includes(before.status) ||
      !before.allowedStatuses?.includes('completed') ||
      !revisionPattern.test(before.revision)
    )
      throw completionError();
    const response = await api.put(
      `/admin/shipments/${id}/status-transition`,
      {
        status: 'completed',
        expectedRevision: before.revision,
      },
      { ...config, skipAuthReplay: true }
    );
    assertCurrentSession(session);
    const after = response.data?.data;
    if (
      after?.shipmentStatusApplied !== true ||
      after._id !== id ||
      after.orderId !== before.orderId ||
      after.orderNumber !== orderNumber ||
      after.status !== 'completed' ||
      !['shipped', 'completed'].includes(after.orderStatus) ||
      after.changedBy !== session.userId ||
      !revisionPattern.test(after.revision) ||
      after.revision === before.revision
    )
      throw completionError();
    return response;
  } catch {
    throw completionError();
  }
}

const shipmentService = {
  // Read-only POST keeps searches out of URLs and returns the linked order's current status.
  getShipments: (params) => api.post('/admin/shipments/search', params),
  completeShipment,
  createShipment: (data) => api.post('/admin/shipments', data),
  updateShipment: (id, data) => api.put(`/admin/shipments/${id}`, data),
  // Removes the shipment from the app; it can be restored. The order it belongs to is untouched.
  deleteShipment: (id) => api.delete(`/admin/shipments/${id}`),
  restoreShipment: (id) => api.post(`/admin/shipments/${id}/restore`),
};

export default shipmentService;
